import {Controller} from '@hotwired/stimulus'

// Reparents the tactical-meeting customize popup's #meeting-menu turbo-frame.
//
// The customize popup pops #meeting-menu onto <body> for modal layering while it
// is open (rendered from meeting_templates/_edit with move_to_body), then restores
// it into #meeting-menu-container when Done/Save/Save As land back on the menu
// index (meeting_components/index). Both views render into the SAME frame, so this
// controller is mounted — once per render — on a hidden marker inside the frame
// content and reads `mode` ('pop' | 'restore') to decide which way to move.
//
// THE MOVE MUST NOT HAPPEN WHILE THE FRAME IS STILL INCOMPLETE. The controller
// connects during the same render that produced the content (Turbo has not set the
// frame's `complete` flag yet). Moving the <turbo-frame> then disconnects/reconnects
// it and re-runs Turbo's #loadSourceURL — whose guard passes on !complete — firing a
// redundant GET that races the in-flight navigation and strands the frame,
// transparent and mispositioned (the GF-3226 Save/Done render break Heather bounced).
// So we defer to turbo:frame-load, which dispatches only after `complete` is set —
// the same reconnect then hits the guard as complete=true and no redundant fetch
// fires. If the controller happens to connect AFTER frame-load already fired (the
// frame is already `complete`), moving immediately is safe by that same criterion.
//
// The DOM move itself is visually invisible: while #meeting-menu carries
// ui-sortable it is position:fixed at a viewport coordinate, so its on-screen
// position does not depend on its DOM parent — deferring by one frame does not
// flash. What makes the restore VISIBLE is the sortable teardown below: jQuery UI
// adds ui-sortable when the popup's sortable() initializes and nothing removes it
// (Turbo replaces frame content, never the frame element), so without a teardown
// the restored menu keeps the popup's fixed 20/20 modal styling and floats over
// the page header. Destroying the widget (not just stripping the class) matters:
// a still-attached widget makes the popup's next sortable({...}) call a setter,
// which would not re-add the class, and the second Customize open would render
// unpositioned.
export default class extends Controller {
  static values = {mode: String}

  connect() {
    this.frame = this.element.closest('turbo-frame') || document.getElementById('meeting-menu')
    this.destination = this.destinationForMode()
    if (!this.frame || !this.destination)
      return
    if (this.alreadyAtDestination()) {
      this.teardownSortableIfRestoring()
      return
    }

    if (this.frame.complete) {
      this.move()
    } else {
      this.onFrameLoad = () => this.move()
      this.frame.addEventListener('turbo:frame-load', this.onFrameLoad, {once: true})
    }
  }

  disconnect() {
    if (this.onFrameLoad && this.frame)
      this.frame.removeEventListener('turbo:frame-load', this.onFrameLoad)
  }

  destinationForMode() {
    if (this.modeValue === 'pop')
      return document.body
    if (this.modeValue === 'restore')
      return document.getElementById('meeting-menu-container')
    return null
  }

  alreadyAtDestination() {
    if (this.modeValue === 'pop')
      return this.frame.parentElement === document.body
    return this.destination.contains(this.frame)
  }

  move() {
    this.destination.appendChild(this.frame)
    this.teardownSortableIfRestoring()
  }

  teardownSortableIfRestoring() {
    if (this.modeValue !== 'restore')
      return
    const $ = window.jQuery
    if ($?.fn?.sortable && $(this.frame).data('ui-sortable'))
      $(this.frame).sortable('destroy')
    this.frame.classList.remove('ui-sortable')
  }
}
